import {
    useState,
    useEffect
} from "react"
import {
    useParams,
    useNavigate
} from "react-router-dom"
import {
    useMutation,
    useQuery,
    useQueryClient
} from "@tanstack/react-query"
import {
    ArrowLeft,
    FileBadge2,
    Clock,
    CheckCircle2,
    User,
    Calendar,
    MapPin,
    Phone,
    Mail,
    Download,
    FileText,
    Ticket
} from "lucide-react"
import { getStatusClasses } from "../../helpers/status-colour-helper"
import consumerDisputeIDDetailService from "./Services/ConsumerRequestQuery"
import ScrollableReadOnlyField from "../../Components/ScrollableReadOnlyField"
import { formatDateTime } from "../../helpers/format-date-time-helper"
import commentQueryService from "./Services/CommentQueryService"
import commentMutationService from "./Services/CommentMutationService"
import reportStatusMutationService from "./Services/ReportStatusMutationService"
import Overlay from "../../Components/Overlay"
import ErrorPopUp from "../../Components/ErrorPopUp"
import SuccessPopup from "../../Components/SuccessPopUp"

const RESOLUTION_STATUS_MAP = {
    "Verified as Accurate": "inProgress",
    "Updated / Modified": "updated",
    "Blocked from Reporting – Legal Requirement": "blocked-legalRequirement",
    "Consumer Withdrew Dispute": "withdrawn",
    "Reinserted After Certification": "reinserted",
    "Deleted – Unable to Verify": "deleted-unableToVerify",
    "Deleted – Incorrect Information": "deleted-incorrectInformation",
}
const RESOLUTION_OUTCOMES = Object.keys(RESOLUTION_STATUS_MAP)
const STATUS_RESOLUTION_MAP = Object.fromEntries(
    Object.entries(RESOLUTION_STATUS_MAP).map(([label, value]) => [value, label])
)
const ALLOWED_TYPES = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx", "epub"]

const ConsumerDisputeReportDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [status, setStatus] = useState("Open")
    const [resolutionOutcome, setResolutionOutcome] = useState("")
    const [comments, setComments] = useState([
        { text: "", createdAt: new Date().toISOString() }
    ])

    const [showSuccess, setShowSuccess] = useState(false)
    const [showError, setShowError] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    const {
        data: request,
    } = useQuery({
        queryKey: ["request-copy-detail", id],
        queryFn: () => consumerDisputeIDDetailService(id),
        enabled: !!id
    })

    const {
        data: existingComments = [],
    } = useQuery({
        queryKey: ["consumer-dispute-comments", id],
        queryFn: () =>
            commentQueryService(id, "consumerDispute"),
        enabled: !!id
    })

    const {
        mutate: mutateComment,
        isPending: commentPending
    } = useMutation({

        mutationFn: commentMutationService,

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["consumer-dispute-comments", id]
            })

            setComments([{ text: "", createdAt: new Date().toISOString() }])
        },

        onError: (error) => {

            console.error("Comment failed", error)

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to add comment."
            )

            setShowError(true)
        }

    })


    const {
        mutate: mutateStatus
    } = useMutation({

        mutationFn: reportStatusMutationService,
        onSuccess: (_, variables) => {

            if (variables.status === "completed")
                setStatus("Completed")

            if (variables.status === "inProgress")
                setStatus("InProgress")

            setShowSuccess(true)
        },

        onError: (error) => {

            console.error("Status update failed", error)

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to update status."
            )

            setShowError(true)
        }

    })


    useEffect(() => {

        if (!request) return
        if (request.status) {
            setStatus(request.status === "completed" ? "Completed" : request.status)
            if (STATUS_RESOLUTION_MAP[request.status]) {
                setResolutionOutcome(
                    STATUS_RESOLUTION_MAP[request.status]
                )
            }
        }
    }, [request])


    if (!request) {
        return (
            <Overlay />
        )
    }

    const documents = request.documents || {
        supportingMaterials: null,
        representativeAuthorization: null
    }

    // Only show fields that have values for Preferred Contact Method
    const contactFields = []
    if (request.email) {
        contactFields.push(
            <div key="email" className="flex items-center gap-4">
                <span className="w-20 font-bold text-black text-sm uppercase tracking-wide">Email:</span>
                <a
                    href={`mailto:${request.email}`}
                    className="text-base font-bold text-blue-600 hover:text-blue-800 hover:underline flex-1 min-w-0 truncate"
                >
                    {request.email}
                </a>
            </div>
        )
    }
    if (request.phone) {
        contactFields.push(
            <div key="phone" className="flex items-center gap-4">
                <span className="w-20 font-bold text-black text-sm uppercase tracking-wide">Phone:</span>
                <span className="text-base font-bold text-black flex-1 min-w-0">{request.phone}</span>
            </div>
        )
    }
    if (request.contactMethod && request.contactMethod !== '') {
        contactFields.push(
            <div key="preferred" className="flex items-center gap-4">
                <span className="w-20 font-bold text-black text-sm uppercase tracking-wide">Mail:</span>
                <span className="text-base font-bold text-black flex-1 min-w-0">{request.contactMethod}</span>
            </div>
        )
    }

    /*COMMENTS LOGIC */
    const addComment = () => {
        if (comments.length < 5) {
            setComments((prev) => [...prev, { text: "", createdAt: new Date().toISOString() }])
        }
    }
    const removeComment = (index) => {
        setComments(comments.filter((_, i) => i !== index))
    }

    const updateComment = (index, value) => {
        setComments(prev =>
            prev.map((c, i) =>
                i === index ? { ...c, text: value } : c
            )
        )
    }

    const handleResolve = (nextStatus) => {

        if (nextStatus === "completed" && !resolutionOutcome)
            return

        const commentList = comments
            .map(c => c.text?.trim())
            .filter(Boolean)

        const statusPayload = {
            reportType: "consumerDispute",
            reportId: id,
            status: nextStatus
        }

        const backendResolution =
            nextStatus === "completed"
                ? RESOLUTION_STATUS_MAP[resolutionOutcome]
                : null

        if (commentList.length > 0) {
            let done = 0
            commentList.forEach((text) => {
                mutateComment(
                    { parentId: id, parentType: "consumerDispute", comment: text, resolutionOutcome: backendResolution },
                    {
                        onSuccess: () => {
                            done++
                            if (done === commentList.length) mutateStatus(statusPayload)
                        },
                    }
                )
            })
        } else if (nextStatus === "completed") {
            mutateComment(
                { parentId: id, parentType: "consumerDispute", comment: "", resolutionOutcome: backendResolution },
                { onSuccess: () => mutateStatus(statusPayload) }
            )
        } else {
            mutateStatus(statusPayload)
        }
    }

    const handleFileOpen = (path) => {
        if (!path) {
            alert("File not available")
            return
        }
        const urlWithoutQuery = path.split("?")[0]
        const extension = urlWithoutQuery.split(".").pop().toLowerCase()

        if (!ALLOWED_TYPES.includes(extension)) {
            alert("This file type is not allowed.")
            return
        }

        const baseURL = window.location.origin
        const cleanBase = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL
        const cleanPath = path.startsWith("/") ? path.slice(1) : path
        const url = path.startsWith("http") ? path : `${cleanBase}/${cleanPath}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    let timestampLabel = "";
    let timestampValue = "";
    if (request?.original.createdAt || request?.original.updatedAt) {
        const created = request?.original.createdAt ? new Date(request.original.createdAt) : null;
        const updated = request?.original.updatedAt ? new Date(request.original.updatedAt) : null;

        if (!updated || created?.getTime() === updated?.getTime()) {
            timestampLabel = "Created At";
            timestampValue = created;
        } else if (updated > created) {
            timestampLabel = "Updated At";
            timestampValue = updated;
        } else {
            timestampLabel = "Created At";
            timestampValue = created;
        }
    }

    return (
        <div className="w-[70vw] max-w-[75vw] mx-auto space-y-8 p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border">
                <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5 text-gray-700" />
                </button>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        <span className="text-zinc-700 text-md">Ticket ID</span> - {request?.original?.referenceId || "N/A"}
                    </h1>
                    <p className="text-lg text-gray-600 font-medium">Consumer Dispute Report</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1.5 text-sm font-semibold border rounded-full ${getStatusClasses(request.status)}`}>
                        {request.status}
                    </span>

                    {timestampValue && (
                        <div className="flex items-center gap-1 text-xs text-black font-bold">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                                <span className="font-semibold text-black">
                                    {timestampLabel}:
                                </span>{" "}
                                {formatDateTime(timestampValue, true)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* 1. PERSONAL INFORMATION - ROW WISE */}
            <section className="bg-white rounded-2xl border shadow-sm p-6">
                <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 mb-6">
                    <User className="h-6 w-6 text-blue-600" />
                    Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ScrollableReadOnlyField
                        label="Service"
                        value={request.service}
                        icon={FileBadge2}
                        className="bg-blue-50/40 border-blue-100"
                    />

                    <ScrollableReadOnlyField
                        label="Ref. No"
                        value={request.caseRefNo}
                        copyable
                        icon={Ticket}
                        className="bg-blue-50/40 border-blue-100"
                    />

                    <ScrollableReadOnlyField
                        label="Full Name"
                        value={request.fullName}
                        icon={User}
                        className="bg-blue-50/40 border-blue-100"
                    />

                    <ScrollableReadOnlyField
                        label="Phone Number"
                        value={request.phone}
                        icon={Phone}
                        className="bg-blue-50/40 border-blue-100"
                    />

                    <ScrollableReadOnlyField
                        label="Date of Birth"
                        value={request.dob && formatDateTime(request.dob)}
                        icon={Calendar}
                        className="bg-blue-50/40 border-blue-100"
                    />
                    <ScrollableReadOnlyField
                        label="Email"
                        value={request.email}
                        icon={Mail}
                        className="bg-blue-50/40 border-blue-100"
                        copyable
                    />
                </div>
            </section >

            {/* 2. ADDRESS INFORMATION */}
            {request.service === "Background Check" && (
                <section className="bg-white rounded-2xl border shadow-sm p-6" >
                    <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 mb-6">
                        <MapPin className="h-6 w-6 text-green-600" />
                        Address Information
                    </h2>
                    <div className="space-y-4">
                        <ScrollableReadOnlyField
                            label="Current Address"
                            value={request.original?.currentAddress || request.currentAddress || "N/A"}
                            icon={MapPin}
                            className="bg-green-50/40 border-green-100"
                        />
                        <ScrollableReadOnlyField
                            label="Previous Address"
                            value={request.original?.previousAddress || request.previousAddress || "N/A"}
                            icon={MapPin}
                            className="bg-green-50/40 border-green-100"
                        />
                    </div>
                </section >)}
            < section className="bg-white rounded-2xl border shadow-sm p-6" >
                <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 mb-6">
                    <MapPin className="h-6 w-6 text-green-600" />
                    Preferred Contact Method *
                </h2>
                <div className="p-4 bg-gray-50 rounded-xl border-2 border-blue-200 min-h-[80px]">
                    {contactFields.length > 0 ? (
                        <div className="space-y-3">
                            {contactFields}
                        </div>
                    ) : (
                        <p className="text-base font-bold text-gray-500 text-center">No contact methods provided</p>
                    )}
                </div>
            </section >

            {/* 3. DISPUTE INFORMATION - NON-EDITABLE */}
            < section className="bg-white rounded-2xl border shadow-sm p-6" >
                <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 mb-6">
                    <FileText className="h-6 w-6 text-purple-600" />
                    Dispute Information
                </h2>
                <div className="space-y-4">
                    <ScrollableReadOnlyField
                        label="End User"
                        value={request.fullName && request.email ? `${request.fullName} (${request.email})` : request.fullName}
                        className="bg-purple-50/30 border-purple-100"
                    />
                    <ScrollableReadOnlyField
                        label="Dispute Description"
                        value={request.original?.disputedInformation || request.disputeDescription}
                        height="max-h-[50rem]"
                        className="bg-purple-50/30 border-purple-100"
                    />
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                            Current Status
                        </label>
                        <div className="p-3 bg-gray-50 rounded-xl border">
                            <span className={`px-3 py-1 text-sm font-semibold border rounded-full ${getStatusClasses(status)}`}>
                                {status}
                            </span>
                        </div>
                    </div>
                </div>
            </section >

            {/* 4. SUPPORTING DOCUMENTS */}
            < section className="bg-white rounded-2xl border shadow-sm p-6" >
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-orange-600" />
                    Optional Supporting Materials
                </h2>
                {
                    !documents.supportingMaterials && !documents.representativeAuthorization && (
                        <p className="text-sm text-gray-500">No documents were uploaded.</p>
                    )
                }
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documents.supportingMaterials && (
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-orange-900">Supporting Document</span>
                                <button
                                    onClick={() => handleFileOpen(documents.supportingMaterials)}
                                    className="p-1 hover:bg-orange-200 rounded-full transition-colors"
                                >
                                    <Download className="h-4 w-4 text-orange-700" />
                                </button>
                            </div>
                        </div>
                    )}
                    {documents.representativeAuthorization && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-purple-900">Authorization Document</span>
                                <button
                                    onClick={() => handleFileOpen(documents.representativeAuthorization)}
                                    className="p-1 hover:bg-purple-200 rounded-full transition-colors"
                                >
                                    <Download className="h-4 w-4 text-purple-700" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section >

            {/* 5. RESOLUTION OUTCOME */}
            < section className="bg-white rounded-2xl border shadow-sm p-6" >
                <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Resolution Outcomes *
                </label>

                {
                    status === "Completed" ? (
                        <div className="w-full p-3 bg-gray-100 border rounded-xl text-gray-800 font-medium">
                            {resolutionOutcome || "No Resolution Available"}
                        </div>
                    ) : (
                        <select
                            className="w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500"
                            value={resolutionOutcome}
                            onChange={(e) => setResolutionOutcome(e.target.value)}
                        >
                            <option value="">——— Select ———</option>
                            {RESOLUTION_OUTCOMES.map((item) => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </select>
                    )
                }
            </section >

            {/* 6. COMMENTS */}
            < section className="bg-white rounded-2xl border shadow-sm p-6 relative" >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-gray-600" />
                        Comments
                    </h2>

                    {comments.length < 5 && (
                        <button
                            onClick={addComment}
                            className="text-blue-600 font-semibold hover:underline"
                        >
                            + Add
                        </button>
                    )}
                </div>

                <div className="space-y-4">
                    {comments.map((comment, index) => (
                        <div key={index} className="relative">
                            <textarea
                                className="w-full p-4 border rounded-xl min-h-[100px]"
                                placeholder={`Comment ${index + 1}`}
                                value={comment.text}
                                onChange={(e) => updateComment(index, e.target.value)}
                            />

                            {comments.length > 1 && (
                                <button
                                    onClick={() => removeComment(index)}
                                    className="absolute top-2 right-2 text-red-500 text-sm hover:underline"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </section >
            {/*output comment with timestamp if createdAt*/}
            {
                existingComments?.length > 0 && (
                    <section className="bg-gray-50 rounded-2xl border p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">
                            Previous Comments
                        </h3>

                        <div className="space-y-3">
                            {existingComments.map((item, index) => (
                                <div key={index} className="p-4 bg-white border rounded-xl">
                                    <p className="text-gray-900 font-medium whitespace-pre-wrap break-words">{item.text}</p>
                                    <p className="text-xs text-gray-500 mt-2">{formatDateTime(item.createdAt, true)}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )
            }
            {/* 7. STATUS ACTIONS */}
            <div className="flex flex-wrap gap-4 p-6 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl border-2 border-dashed border-gray-200">
                {request.status === "completed" ? (
                    <div className="flex items-center gap-2 px-6 py-3 bg-green-500/10 text-green-700 font-semibold rounded-xl border border-green-300/40">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        Completed
                    </div>
                ) : (
                    <>
                        <button
                            className="flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-200 shadow-md bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => handleResolve("inProgress")}
                        >
                            <Clock className="h-5 w-5" />
                            Mark In Progress
                        </button>
                        <button
                            className="flex items-center gap-2 px-6 py-3 bg-blue-700 hover:bg-blue-500 text-white font-semibold rounded-xl transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleResolve("completed")}
                            disabled={!resolutionOutcome}
                        >
                            <CheckCircle2 className="h-5 w-5" />
                            Mark Done
                        </button>
                    </>
                )}

            </div>
            {commentPending && <Overlay />}
            {
                showSuccess && (
                    <SuccessPopup
                        title="Status Updated"
                        message="Consumer dispute status updated successfully."
                        onClose={() => setShowSuccess(false)}
                    />

                )
            }
            {
                showError && (
                    <ErrorPopUp
                        title="Operation Failed"
                        message={errorMessage}
                        onClose={() => setShowError(false)}
                    />
                )
            }
        </div >
    )
}

export default ConsumerDisputeReportDetail
